/** @typedef {import('../models/registryTypes').RegistryEntry} RegistryEntry */
/** @typedef {import('../models/registryTypes').RoomMember} RoomMember */

import { createPlayer, ensurePlayerForUser } from "../../../services/identityService";
import { validateDisplayName } from "../../../domain/identity/playerNameSearch";
import { resolveArenaRosterLabel } from "../../../domain/identity/playerNameParts";
import { getCurrentAuthUser } from "../../../repositories/authRepository";
import { newRegistryEntryId, newRoomMemberId } from "../utils/ids";
import { normRegistryEntries, normRoomMembers } from "../utils/normalize";

/**
 * @param {RegistryEntry[]} entries
 * @param {string} playerId
 * @param {string} userId
 */
export function findRegistryEntryForUser(entries, playerId, userId) {
  return (
    entries.find(
      (e) =>
        e.active !== false &&
        ((userId && e.userId === userId) || (playerId && e.playerId === playerId))
    ) || null
  );
}

/**
 * Any roster row for user/player, including host-removed (active: false).
 * @param {RegistryEntry[]} entries
 * @param {string} [playerId]
 * @param {string} [userId]
 */
export function findAnyRegistryEntryForUser(entries, playerId, userId) {
  return (
    (entries || []).find(
      (e) =>
        (userId && e.userId === userId) || (playerId && e.playerId === playerId)
    ) || null
  );
}

/**
 * @param {RoomMember[]} members
 * @param {string} userId
 */
export function findMemberForUser(members, userId) {
  return members.find((m) => m.userId === userId) || null;
}

/**
 * Presence-only join (spectators and referees) — no registry row.
 * @param {Object} input
 * @param {string} input.roomId
 * @param {RoomMember[]} input.members
 * @param {string} input.userId
 * @param {'referee' | 'spectator'} input.sessionRole
 */
export function ensurePresenceOnlyMember(input) {
  const roomId = input.roomId;
  let members = normRoomMembers(input.members, roomId);
  const userId = input.userId;
  if (!roomId || !userId) return { members, changed: false };

  const role = input.sessionRole === "spectator" ? "spectator" : "referee";
  const existingMember = findMemberForUser(members, userId);
  let changed = false;

  if (!existingMember) {
    members = [
      {
        id: newRoomMemberId(),
        roomId,
        userId,
        registryEntryId: null,
        role,
        presence: "online",
        joinedAt: Date.now(),
      },
      ...members,
    ];
    changed = true;
  } else {
    const next = {
      ...existingMember,
      registryEntryId: null,
      role,
      presence: "online",
    };
    if (
      next.registryEntryId !== existingMember.registryEntryId ||
      next.role !== existingMember.role ||
      next.presence !== existingMember.presence
    ) {
      members = members.map((m) => (m.id === existingMember.id ? next : m));
      changed = true;
    }
  }

  return { members, changed };
}

/**
 * @param {Object} input
 * @param {string} input.roomId
 * @param {RegistryEntry[]} input.entries
 * @param {RoomMember[]} input.members
 * @param {string} input.userId
 * @param {string} input.displayName
 * @param {'creator' | 'joined'} input.method
 * @param {'referee' | 'player'} input.memberRole
 */
async function ensureUserOnRoster(input) {
  const roomId = input.roomId;
  let rosterEntries = normRegistryEntries(input.entries, roomId);
  let members = normRoomMembers(input.members, roomId);
  const userId = input.userId;
  let changed = false;

  const player = await ensurePlayerForUser(userId, input.displayName || "Player");
  const playerId = player.playerId;
  const authUser = getCurrentAuthUser();
  const isSelf = Boolean(authUser?.uid && authUser.uid === userId);
  // Profile name → Google/auth name → userid — never stick on "Player".
  const displayName = resolveArenaRosterLabel(player, input.displayName, {
    accountEmail: isSelf ? authUser.email : null,
    authUser: isSelf ? authUser : null,
  });

  // Host remove/kick leaves active:false — do not revive or duplicate.
  const prior = findAnyRegistryEntryForUser(rosterEntries, playerId, userId);
  if (prior && prior.active === false && !input.reactivateInactive) {
    return { entries: rosterEntries, members, changed: false };
  }

  let entry = findRegistryEntryForUser(rosterEntries, playerId, userId);

  if (!entry) {
    entry = {
      id: newRegistryEntryId(),
      roomId,
      playerId,
      displayName,
      avatar: null,
      userId,
      addedAt: Date.now(),
      addedBy: userId,
      addedMethod: input.method,
      active: true,
    };
    rosterEntries = [entry, ...rosterEntries];
    changed = true;
  } else if (entry.displayName !== displayName || entry.userId !== userId) {
    rosterEntries = rosterEntries.map((e) =>
      e.id === entry.id ? { ...e, displayName, userId, active: true } : e
    );
    entry = rosterEntries.find((e) => e.id === entry.id);
    changed = true;
  }

  const memberRole = input.memberRole === "referee" ? "referee" : "player";
  const existingMember = findMemberForUser(members, userId);
  if (!existingMember) {
    members = [
      {
        id: newRoomMemberId(),
        roomId,
        userId,
        registryEntryId: entry.id,
        role: memberRole,
        presence: "online",
        joinedAt: Date.now(),
      },
      ...members,
    ];
    changed = true;
  } else {
    const next = {
      ...existingMember,
      registryEntryId: entry.id,
      role: memberRole,
      presence: "online",
    };
    if (
      next.registryEntryId !== existingMember.registryEntryId ||
      next.role !== existingMember.role ||
      next.presence !== existingMember.presence
    ) {
      members = members.map((m) => (m.id === existingMember.id ? next : m));
      changed = true;
    }
  }

  return { entries: rosterEntries, members, changed };
}

/**
 * @param {Object} input
 * @param {string} input.roomId
 * @param {RegistryEntry[]} input.entries
 * @param {RoomMember[]} input.members
 * @param {string} input.userId
 * @param {string} input.displayName
 * @param {'creator' | 'joined'} input.method
 * @param {'referee' | 'player' | 'spectator'} input.sessionRole
 * @param {boolean} [input.refereeOnRoster] When sessionRole is referee, also add RegistryEntry
 * @returns {Promise<{ entries: RegistryEntry[]; members: RoomMember[]; changed: boolean }>}
 */
export async function ensureConnectedUserInRegistry(input) {
  const roomId = input.roomId;
  const entries = normRegistryEntries(input.entries, roomId);
  let members = normRoomMembers(input.members, roomId);
  const userId = input.userId;
  if (!roomId || !userId) return { entries, members, changed: false };

  const sessionRole = input.sessionRole || "player";
  /** Joining another host's room: shell may be spectator; roster persona is always player. */
  const rosterRole =
    input.method === "joined" && sessionRole === "spectator"
      ? "player"
      : sessionRole;

  if (rosterRole === "spectator") {
    const presence = ensurePresenceOnlyMember({
      roomId,
      members,
      userId,
      sessionRole,
    });
    return {
      entries,
      members: presence.members,
      changed: presence.changed,
    };
  }

  if (sessionRole === "referee") {
    if (input.refereeOnRoster) {
      return ensureUserOnRoster({
        roomId,
        entries,
        members,
        userId,
        displayName: input.displayName,
        method: input.method,
        memberRole: "referee",
      });
    }
    const existingRosterEntry = findRegistryEntryForUser(entries, null, userId);
    if (existingRosterEntry) {
      return ensureUserOnRoster({
        roomId,
        entries,
        members,
        userId,
        displayName: input.displayName,
        method: input.method,
        memberRole: "referee",
      });
    }
    const presence = ensurePresenceOnlyMember({
      roomId,
      members,
      userId,
      sessionRole: "referee",
    });
    return {
      entries,
      members: presence.members,
      changed: presence.changed,
    };
  }

  return ensureUserOnRoster({
    roomId,
    entries,
    members,
    userId,
    displayName: input.displayName,
    method: input.method,
    memberRole: "player",
  });
}

/**
 * Add a known linked player to the roster without identity writes.
 * Used when the host syncs an accepted invite (cannot call ensurePlayerForUser for another uid).
 *
 * @param {Object} input
 * @param {string} input.roomId
 * @param {RegistryEntry[]} input.entries
 * @param {RoomMember[]} input.members
 * @param {string} input.userId
 * @param {string} input.playerId
 * @param {string} input.displayName
 * @param {string} [input.addedBy]
 * @param {'creator' | 'joined'} [input.method]
 * @returns {{ entries: RegistryEntry[]; members: RoomMember[]; changed: boolean }}
 */
export function ensureKnownLinkedPlayerOnRoster(input) {
  const roomId = input.roomId;
  let rosterEntries = normRegistryEntries(input.entries, roomId);
  let members = normRoomMembers(input.members, roomId);
  const userId = String(input.userId || "").trim();
  const playerId = String(input.playerId || "").trim();
  const displayName =
    String(input.displayName || "").trim() || "Player";
  if (!roomId || !userId || !playerId) {
    return { entries: rosterEntries, members, changed: false };
  }

  let changed = false;

  // Host remove/kick leaves active:false — do not revive or add a second row.
  const prior = findAnyRegistryEntryForUser(rosterEntries, playerId, userId);
  if (prior && prior.active === false) {
    if (!input.reactivateInactive) {
      return { entries: rosterEntries, members, changed: false };
    }
    rosterEntries = rosterEntries.map((e) =>
      e.id === prior.id
        ? {
            ...e,
            displayName,
            userId,
            playerId,
            active: true,
            addedMethod: input.method === "creator" ? "creator" : "joined",
          }
        : e
    );
    const entry = rosterEntries.find((e) => e.id === prior.id);
    let membersNext = members;
    const existingMember = findMemberForUser(membersNext, userId);
    if (!existingMember) {
      membersNext = [
        {
          id: newRoomMemberId(),
          roomId,
          userId,
          registryEntryId: entry.id,
          role: "player",
          presence: "online",
          joinedAt: Date.now(),
        },
        ...membersNext,
      ];
    } else {
      membersNext = membersNext.map((m) =>
        m.userId === userId
          ? {
              ...m,
              registryEntryId: entry.id,
              role: "player",
              presence: "online",
            }
          : m
      );
    }
    return { entries: rosterEntries, members: membersNext, changed: true };
  }

  let entry = findRegistryEntryForUser(rosterEntries, playerId, userId);

  if (!entry) {
    entry = {
      id: newRegistryEntryId(),
      roomId,
      playerId,
      displayName,
      avatar: null,
      userId,
      addedAt: Date.now(),
      addedBy: String(input.addedBy || userId),
      addedMethod: input.method === "creator" ? "creator" : "joined",
      active: true,
    };
    rosterEntries = [entry, ...rosterEntries];
    changed = true;
  } else if (entry.displayName !== displayName || entry.userId !== userId) {
    rosterEntries = rosterEntries.map((e) =>
      e.id === entry.id
        ? { ...e, displayName, userId, playerId, active: true }
        : e
    );
    entry = rosterEntries.find((e) => e.id === entry.id);
    changed = true;
  }

  const existingMember = findMemberForUser(members, userId);
  if (!existingMember) {
    members = [
      {
        id: newRoomMemberId(),
        roomId,
        userId,
        registryEntryId: entry.id,
        role: "player",
        presence: "online",
        joinedAt: Date.now(),
      },
      ...members,
    ];
    changed = true;
  } else {
    const next = {
      ...existingMember,
      registryEntryId: entry.id,
      role: "player",
      presence: "online",
    };
    if (
      next.registryEntryId !== existingMember.registryEntryId ||
      next.role !== existingMember.role ||
      next.presence !== existingMember.presence
    ) {
      members = members.map((m) => (m.id === existingMember.id ? next : m));
      changed = true;
    }
  }

  return { entries: rosterEntries, members, changed };
}

/**
 * @param {Object} input
 * @param {string} input.roomId
 * @param {RegistryEntry[]} input.entries
 * @param {string} input.displayName
 * @param {string} input.addedByUid
 * @returns {Promise<{ entries: RegistryEntry[]; entry: RegistryEntry }>}
 */
export async function addManualRegistryPlayer(input) {
  const roomId = input.roomId;
  const entries = normRegistryEntries(input.entries, roomId);
  const validated = validateDisplayName(input.displayName || "");
  if (!validated.ok) {
    throw new Error(validated.error || "Enter a player name");
  }
  const displayName = validated.displayName;
  const dup = entries.some(
    (e) => e.active !== false && e.displayName.toLowerCase() === displayName.toLowerCase()
  );
  if (dup) {
    throw new Error("A player with this name is already in the registry");
  }

  const player = await createPlayer({
    displayName,
    createdBy: input.addedByUid,
    userId: null,
  });

  const entry = {
    id: newRegistryEntryId(),
    roomId,
    playerId: player.playerId,
    displayName: player.displayName,
    avatar: null,
    userId: null,
    addedAt: Date.now(),
    addedBy: input.addedByUid,
    addedMethod: "manual",
    active: true,
  };

  return { entries: [entry, ...entries], entry };
}

/**
 * @param {RoomMember[]} members
 * @param {string} userId
 */
export function markMemberOffline(members, userId) {
  const member = findMemberForUser(members, userId);
  if (!member || member.presence === "offline") return { members, changed: false };
  return {
    members: members.map((m) =>
      m.userId === userId ? { ...m, presence: "offline" } : m
    ),
    changed: true,
  };
}

/**
 * @param {RegistryEntry[]} entries
 * @param {string} entryId
 */
export function deactivateRegistryEntry(entries, entryId) {
  return entries.map((e) => (e.id === entryId ? { ...e, active: false } : e));
}

/**
 * @param {RegistryEntry[]} entries
 * @param {RoomMember[]} members
 * @param {string} entryId
 * @returns {{ entries: RegistryEntry[]; members: RoomMember[]; changed: boolean; kickedUserId: string | null }}
 */
export function kickPlayerFromRoster(entries, members, entryId) {
  const entry = entries.find((e) => e.id === entryId && e.active !== false);
  if (!entry) {
    return { entries, members, changed: false, kickedUserId: null };
  }
  const nextEntries = deactivateRegistryEntry(entries, entryId);
  let nextMembers = members;
  let kickedUserId = null;
  if (entry.userId) {
    kickedUserId = entry.userId;
    nextMembers = members.filter((m) => m.userId !== entry.userId);
  }
  return {
    entries: nextEntries,
    members: nextMembers,
    changed: true,
    kickedUserId,
  };
}

/**
 * @param {Array<{ memberEntryIds?: string[] }>} teams
 * @param {string} entryId
 */
export function teamsWithoutRegistryEntry(teams, entryId) {
  return (teams || []).map((t) => ({
    ...t,
    memberEntryIds: (t.memberEntryIds || []).filter((id) => id !== entryId),
  }));
}

/**
 * Presence map for roster UI (entry id → presence).
 * @param {RegistryEntry[]} entries
 * @param {RoomMember[]} members
 */
export function presenceByRegistryEntryId(entries, members) {
  /** @type {Record<string, string>} */
  const byUser = {};
  for (const m of members) {
    byUser[m.userId] = m.presence;
  }
  /** @type {Record<string, string>} */
  const out = {};
  for (const e of entries) {
    if (e.userId && byUser[e.userId]) {
      out[e.id] = byUser[e.userId];
    }
  }
  return out;
}

// TODO: Invite local player → link registry entry to invitation token
// TODO: Claim local player → attach userId to entry + merge players collection
// TODO: Tournament/club bulk import → addedMethod: 'import'
